import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Pinch to zoom, and drag to pan once zoomed.
 *
 * Done by hand rather than handed to the browser. `touch-action: pinch-zoom`
 * asks for the *page's* zoom, and a page cannot be zoomed while an element is
 * fullscreen — the browser suppresses it for the whole presentation. So the one
 * place a photo most needs zooming is the one place the built-in gesture is
 * guaranteed not to arrive, and the only way to have it is to track the pointers
 * and drive the transform ourselves.
 *
 * The surface must therefore hold `touch-action: none` while this is enabled:
 * every gesture here is ours, and a browser that claims part of one takes the
 * pointer stream with it.
 */

const MAX_SCALE = 5;
/** Below this the pinch is treated as an attempt to get back to 1:1. */
const SNAP_BACK = 1.05;

interface Point {
  x: number;
  y: number;
}

const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
const midpoint = (a: Point, b: Point) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

export interface PinchZoom {
  handlers: React.HTMLAttributes<HTMLElement>;
  /** Ready to drop on `style.transform`. Identity when not zoomed. */
  transform: string;
  /** True once magnified — the caller suppresses paging, since a drag now pans. */
  zoomed: boolean;
  reset: () => void;
}

export function usePinchZoom({ enabled }: { enabled: boolean }): PinchZoom {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 });

  /** Live pointers, by id. Two of them make a pinch; one pans. */
  const pointers = useRef(new Map<number, Point>());
  /** The state a gesture started from, so it can be applied as a delta. */
  const gesture = useRef<{ distance: number; mid: Point; scale: number; offset: Point } | null>(
    null,
  );
  /** The surface being pinched, for the pan bounds. */
  const elementRef = useRef<Element | null>(null);

  /**
   * The live transform, mirrored out of state.
   *
   * The handlers cannot read `scale`/`offset` directly: those are one render
   * behind whenever the moves and the release land in the same batch, and a
   * release that compares a stale `scale` against the snap-back threshold sees
   * 1 and throws the zoom away the instant you lift your fingers.
   */
  const live = useRef<{ scale: number; offset: Point }>({ scale: 1, offset: { x: 0, y: 0 } });

  const apply = useCallback((nextScale: number, nextOffset: Point) => {
    live.current = { scale: nextScale, offset: nextOffset };
    setScale(nextScale);
    setOffset(nextOffset);
  }, []);

  const reset = useCallback(() => {
    pointers.current.clear();
    gesture.current = null;
    apply(1, { x: 0, y: 0 });
  }, [apply]);

  // Leaving fullscreen with a photo still magnified would strand it that way
  // inside a card the zoom was never meant for.
  useEffect(() => {
    if (!enabled) reset();
  }, [enabled, reset]);

  /**
   * Keep the magnified image covering its frame. Panning is only meaningful
   * across the overflow — past that you are dragging the photo off its own
   * window, which reads as the gesture being broken rather than as freedom.
   */
  const clamp = (next: Point, atScale: number): Point => {
    const el = elementRef.current;
    if (!el) return next;
    const limitX = (el.clientWidth * (atScale - 1)) / 2;
    const limitY = (el.clientHeight * (atScale - 1)) / 2;
    return {
      x: Math.max(-limitX, Math.min(limitX, next.x)),
      y: Math.max(-limitY, Math.min(limitY, next.y)),
    };
  };

  const begin = () => {
    const [a, b] = [...pointers.current.values()];
    if (!a || !b) return;
    gesture.current = {
      distance: distance(a, b),
      mid: midpoint(a, b),
      scale: live.current.scale,
      offset: live.current.offset,
    };
  };

  const handlers: React.HTMLAttributes<HTMLElement> = {
    onPointerDown: (e: React.PointerEvent) => {
      if (!enabled) return;
      elementRef.current = e.currentTarget;
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.current.size === 2) begin();
      else if (pointers.current.size === 1 && live.current.scale > 1) {
        gesture.current = {
          distance: 0,
          mid: { x: e.clientX, y: e.clientY },
          scale: live.current.scale,
          offset: live.current.offset,
        };
      }
    },

    onPointerMove: (e: React.PointerEvent) => {
      if (!enabled || !pointers.current.has(e.pointerId)) return;
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const from = gesture.current;
      if (!from) return;

      const active = [...pointers.current.values()];
      if (active.length >= 2) {
        const [a, b] = active;
        if (!a || !b) return;
        const next = Math.min(
          MAX_SCALE,
          Math.max(1, (from.scale * distance(a, b)) / from.distance),
        );
        const mid = midpoint(a, b);
        apply(
          next,
          clamp(
            {
              x: from.offset.x + (mid.x - from.mid.x),
              y: from.offset.y + (mid.y - from.mid.y),
            },
            next,
          ),
        );
        return;
      }

      // One finger, already magnified: pan.
      if (from.scale > 1) {
        apply(
          from.scale,
          clamp(
            {
              x: from.offset.x + (e.clientX - from.mid.x),
              y: from.offset.y + (e.clientY - from.mid.y),
            },
            from.scale,
          ),
        );
      }
    },

    onPointerUp: (e: React.PointerEvent) => {
      pointers.current.delete(e.pointerId);
      gesture.current = null;
      // Almost-1x means they were pinching their way back, so finish the job
      // rather than leaving the photo a hair off true.
      if (live.current.scale <= SNAP_BACK) {
        apply(1, { x: 0, y: 0 });
      } else if (pointers.current.size === 1) {
        // A finger lifted mid-pinch — the remaining one continues as a pan.
        const [only] = [...pointers.current.values()];
        if (only) {
          gesture.current = {
            distance: 0,
            mid: only,
            scale: live.current.scale,
            offset: live.current.offset,
          };
        }
      }
    },

    onPointerCancel: (e: React.PointerEvent) => {
      pointers.current.delete(e.pointerId);
      gesture.current = null;
    },
  };

  return {
    handlers,
    transform: scale === 1 ? "none" : `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
    zoomed: scale > 1,
    reset,
  };
}
